import type { SxProps, Theme } from "@mui/material/styles";
import { SEEDIT_FONT_FAMILY_MONO } from "../../themes/seedit-theme.tokens";

const cardTransition = (theme: Theme) =>
  theme.transitions.create(["background-color", "box-shadow"], {
    duration: theme.transitions.duration.short
  });

export const dashboardCardSx = (theme: Theme): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  p: 1.5,
  mb: 1.5,
  transition: cardTransition(theme),
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none"
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
});

export const dashboardKpiGridItemSx = (isMobile: boolean): SxProps<Theme> =>
  isMobile
    ? { minWidth: "inherit", width: "100%" }
    : { minWidth: 260, flex: "1 1 260px", maxWidth: "100%" };

/** Fixed-width TopList tiles — do not grow when only one item is shown. */
export const TOP_LIST_ITEM_WIDTH_PX = 260;

/** Responsive card grid — even gaps, wraps naturally (no space-between stretch). */
export const topListGridParentSx = (isMobile: boolean): SxProps<Theme> => ({
  width: "100%",
  display: "grid",
  gridTemplateColumns: isMobile
    ? "1fr"
    : `repeat(auto-fill, minmax(${TOP_LIST_ITEM_WIDTH_PX}px, 1fr))`,
  gap: 2,
  alignItems: "stretch"
});

export const topListKpiGridItemSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  width: "100%",
  boxSizing: "border-box"
};

export const topListPaperSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboardCardSx(theme),
  overflow: "hidden",
  p: 0,
  alignItems: "stretch",
  width: "100%",
  minWidth: "100%",
  height: "100%",
  boxSizing: "border-box"
});

export const topListCardContainerSx: SxProps<Theme> = {
  width: "100%",
  height: "100%"
};

export const topListHeroImageHeightPx = 92;

export const topListHeroMediaSx: SxProps<Theme> = {
  position: "relative",
  width: "100%",
  height: topListHeroImageHeightPx,
  flexShrink: 0,
  bgcolor: "action.hover"
};

export const topListHeroImageSx: SxProps<Theme> = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block"
};

export const topListHeroOverlaySx: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  background: theme =>
    `linear-gradient(to top, ${theme.palette.mode === "dark" ? "rgba(0,0,0,0.45)" : "rgba(15,23,42,0.28)"} 0%, transparent 60%)`,
  pointerEvents: "none"
};

export const topListHeroContentSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  px: 1.5,
  pb: 1.5,
  pt: 1.25
};

export const topListHeroTitleSx: SxProps<Theme> = {
  fontWeight: 600,
  lineHeight: 1.3,
  mb: 0.5
};

export const topListBookNowRowSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
  mb: 0.5
};

export const dashboardMetricTypographySx: SxProps<Theme> = {
  fontFamily: SEEDIT_FONT_FAMILY_MONO,
  fontWeight: 600
};

export const dashboardInteractiveFocusSx: SxProps<Theme> = {
  cursor: "pointer",
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "info.main",
    outlineOffset: 2
  }
};
